/**
 * Hole In One Golf — Product Media Switcher & PDP Actions
 * Vanilla JS for PDP gallery thumbnail switching and inline share feedback.
 */

(function () {
  'use strict';

  function initProductMedia() {
    document.addEventListener('click', function (e) {
      const thumb = e.target.closest('[data-media-thumbnail]');
      if (!thumb) return;

      e.preventDefault();
      const newSrc = thumb.getAttribute('data-media-thumbnail');
      const newAlt = thumb.getAttribute('data-media-alt') || '';
      const mainImg = document.querySelector('[data-main-media-image]');

      if (mainImg && newSrc) {
        mainImg.src = newSrc;
        mainImg.alt = newAlt;
      }

      const allThumbs = document.querySelectorAll('[data-media-thumbnail]');
      allThumbs.forEach(function (t) {
        t.classList.remove('is-active');
        t.style.borderColor = 'var(--border-subtle)';
      });

      thumb.classList.add('is-active');
      thumb.style.borderColor = 'var(--border-brand)';
    });
  }

  function initShareButton() {
    document.addEventListener('click', function (e) {
      const shareBtn = e.target.closest('[data-share-button]');
      if (!shareBtn) return;

      e.preventDefault();
      const shareText = shareBtn.getAttribute('data-text-share') || 'Share';
      const copiedText = shareBtn.getAttribute('data-text-copied') || 'Link copied';
      const failedText = shareBtn.getAttribute('data-text-failed') || 'Copy the link from the address bar';
      const label = shareBtn.querySelector('[data-share-label]');

      function showFeedback(text) {
        if (label) label.textContent = text;
        setTimeout(function () {
          if (label) label.textContent = shareText;
        }, 2000);
      }

      function onSuccess() {
        showFeedback(copiedText);
      }

      function onFailure() {
        showFeedback(failedText);
      }

      if (navigator.share) {
        navigator.share({ title: document.title, url: window.location.href })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
              navigator.clipboard.writeText(window.location.href)
                .then(onSuccess)
                .catch(onFailure);
            } else {
              onFailure();
            }
          });
      } else if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(window.location.href)
          .then(onSuccess)
          .catch(onFailure);
      } else {
        onFailure();
      }
    });
  }

  function initPdpTabs() {
    const tablists = document.querySelectorAll('[role="tablist"]');
    tablists.forEach(function (tablist) {
      const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      function selectTab(newTab) {
        tabs.forEach(function (tab) {
          const isSelected = (tab === newTab);
          tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          tab.setAttribute('tabindex', isSelected ? '0' : '-1');
          tab.classList.toggle('hio-tag--selected', isSelected);

          const panelId = tab.getAttribute('aria-controls');
          if (panelId) {
            const panel = document.getElementById(panelId);
            if (panel) {
              if (isSelected) {
                panel.removeAttribute('hidden');
              } else {
                panel.setAttribute('hidden', '');
              }
            }
          }
        });
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function (e) {
          e.preventDefault();
          selectTab(tab);
        });
      });

      const initialTab = tabs.find(function (t) {
        return t.getAttribute('aria-selected') === 'true';
      }) || tabs[0];
      selectTab(initialTab);

      tablist.addEventListener('keydown', function (e) {
        const currentTab = document.activeElement;
        const index = tabs.indexOf(currentTab);
        if (index === -1) return;

        let nextIndex = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          nextIndex = (index + 1) % tabs.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          nextIndex = (index - 1 + tabs.length) % tabs.length;
        } else if (e.key === 'Home') {
          nextIndex = 0;
        } else if (e.key === 'End') {
          nextIndex = tabs.length - 1;
        }

        if (nextIndex !== null) {
          e.preventDefault();
          const targetTab = tabs[nextIndex];
          targetTab.focus();
          selectTab(targetTab);
        }
      });
    });
  }

  function toSentenceCase(str) {
    if (!str) return '';
    const clean = str.replace(/:\s*$/, '').trim();
    if (!clean) return '';
    return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
  }

  function detectLeadIn(firstUl) {
    let candidateEl = firstUl.previousElementSibling;
    while (candidateEl && (!candidateEl.textContent || candidateEl.textContent.trim().length === 0)) {
      candidateEl = candidateEl.previousElementSibling;
    }
    if (!candidateEl) return null;

    let sibling = firstUl.previousSibling;
    while (sibling && sibling !== candidateEl) {
      if (sibling.nodeType === 3 && sibling.textContent.trim().length > 0) {
        return null;
      }
      sibling = sibling.previousSibling;
    }

    const rawText = (candidateEl.textContent || '').replace(/\s+/g, ' ').trim();
    if (!rawText || rawText.length === 0 || rawText.length >= 40) {
      return null;
    }

    if (/[.?]$/.test(rawText)) {
      return null;
    }

    const isHeadingTag = /^h[1-6]$/i.test(candidateEl.tagName);
    const endsWithColon = rawText.endsWith(':');

    if (endsWithColon || isHeadingTag || !/[.!?]$/.test(rawText)) {
      return {
        element: candidateEl,
        rawText: rawText,
        heading: toSentenceCase(rawText)
      };
    }

    return null;
  }

  function initProductDescriptionFeatures() {
    const container = document.querySelector('[data-product-description-container]');
    if (!container) return;
    const prose = container.querySelector('[data-product-description-prose]');
    const features = container.querySelector('[data-product-description-features]');
    if (!prose || !features) return;

    const firstUl = prose.querySelector('ul');
    if (!firstUl) return;

    const FLAG_ICON_SVG = '<svg class="hio-icon hio-icon--flag" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 22V4a1 1 0 0 1 .4-.8A6 6 0 0 1 8 2c3 0 5 2 7.333 2q2 0 3.067-.8A1 1 0 0 1 20 4v10a1 1 0 0 1-.4.8A6 6 0 0 1 16 16c-3 0-5-2-8-2a6 6 0 0 0-4 1.528" /></svg>';

    const defaultHeading = features.getAttribute('data-heading') || "Why you'll love it";
    let headingText = defaultHeading;

    const leadIn = detectLeadIn(firstUl);
    if (leadIn) {
      headingText = leadIn.heading;
      if (leadIn.element.parentNode) {
        leadIn.element.parentNode.removeChild(leadIn.element);
      }
    }

    const headingEl = document.createElement('h3');
    headingEl.className = 'hio-product-description__features-heading';
    headingEl.textContent = headingText;

    const directLis = firstUl.querySelectorAll(':scope > li');
    directLis.forEach(function (li) {
      li.classList.add('hio-product-description__features-item');

      const textSpan = document.createElement('span');
      textSpan.className = 'hio-product-description__features-text';
      while (li.firstChild) {
        textSpan.appendChild(li.firstChild);
      }

      const iconSpan = document.createElement('span');
      iconSpan.className = 'hio-product-description__features-icon';
      iconSpan.innerHTML = FLAG_ICON_SVG;

      li.appendChild(iconSpan);
      li.appendChild(textSpan);
    });

    features.innerHTML = '';
    features.appendChild(headingEl);
    firstUl.classList.add('hio-product-description__features-list');
    features.appendChild(firstUl);
    features.removeAttribute('hidden');

    const hasMeaningfulProse = (prose.textContent || '').replace(/\s+/g, ' ').trim().length > 0;
    if (hasMeaningfulProse) {
      container.classList.remove('hio-product-description__grid--single');
    } else {
      prose.setAttribute('hidden', '');
      container.classList.add('hio-product-description__grid--single');
    }
  }

  function init() {
    initProductMedia();
    initShareButton();
    initPdpTabs();
    initProductDescriptionFeatures();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
